import { api } from '@/lib/axios';
import type {
  TimeProjectRef,
  TimeProjectPositionRef,
  TimeProjectStepRef,
  TimeActivityRef,
  TimeUserRef,
  ListParams,
  PaginatedResponse,
  RefSchemasResponse,
  UpdateRefSchemasPayload,
} from '../types/time-master-data.types';

// ── Projects ────────────────────────────────────────────────────────────────

export async function listProjectRefs(params: ListParams = {}) {
  const res = await api.get<PaginatedResponse<TimeProjectRef>>('/time-master-data/projects', { params });
  return res.data;
}
export async function getProjectRef(id: string) {
  const res = await api.get<{ data: TimeProjectRef }>(`/time-master-data/projects/${id}`);
  return res.data.data;
}
export async function createProjectRef(payload: Partial<TimeProjectRef>) {
  const res = await api.post<{ data: TimeProjectRef }>('/time-master-data/projects', payload);
  return res.data.data;
}
export async function updateProjectRef(id: string, payload: Partial<TimeProjectRef>) {
  const res = await api.patch<{ data: TimeProjectRef }>(`/time-master-data/projects/${id}`, payload);
  return res.data.data;
}
export async function deleteProjectRef(id: string) {
  const res = await api.delete<{ data: TimeProjectRef }>(`/time-master-data/projects/${id}`);
  return res.data.data;
}

// ── Positions ───────────────────────────────────────────────────────────────

export async function listPositionRefs(params: ListParams & { projectRefId?: string } = {}) {
  const res = await api.get<PaginatedResponse<TimeProjectPositionRef>>('/time-master-data/positions', { params });
  return res.data;
}
export async function getPositionRef(id: string) {
  const res = await api.get<{ data: TimeProjectPositionRef }>(`/time-master-data/positions/${id}`);
  return res.data.data;
}
export async function createPositionRef(payload: Partial<TimeProjectPositionRef>) {
  const res = await api.post<{ data: TimeProjectPositionRef }>('/time-master-data/positions', payload);
  return res.data.data;
}
export async function updatePositionRef(id: string, payload: Partial<TimeProjectPositionRef>) {
  const res = await api.patch<{ data: TimeProjectPositionRef }>(`/time-master-data/positions/${id}`, payload);
  return res.data.data;
}
export async function deletePositionRef(id: string) {
  const res = await api.delete<{ data: TimeProjectPositionRef }>(`/time-master-data/positions/${id}`);
  return res.data.data;
}

// ── Steps ───────────────────────────────────────────────────────────────────

export async function listStepRefs(
  params: ListParams & { projectRefId?: string; positionRefId?: string; kategorieTxt?: string } = {},
) {
  const res = await api.get<PaginatedResponse<TimeProjectStepRef>>('/time-master-data/steps', { params });
  return res.data;
}
export async function getStepRef(id: string) {
  const res = await api.get<{ data: TimeProjectStepRef }>(`/time-master-data/steps/${id}`);
  return res.data.data;
}
export async function createStepRef(payload: Partial<TimeProjectStepRef>) {
  const res = await api.post<{ data: TimeProjectStepRef }>('/time-master-data/steps', payload);
  return res.data.data;
}
export async function updateStepRef(id: string, payload: Partial<TimeProjectStepRef>) {
  const res = await api.patch<{ data: TimeProjectStepRef }>(`/time-master-data/steps/${id}`, payload);
  return res.data.data;
}
export async function deleteStepRef(id: string) {
  const res = await api.delete<{ data: TimeProjectStepRef }>(`/time-master-data/steps/${id}`);
  return res.data.data;
}

// ── Activity types ──────────────────────────────────────────────────────────

export async function listActivityRefs(params: ListParams = {}) {
  const res = await api.get<PaginatedResponse<TimeActivityRef>>('/time-master-data/activity-types', { params });
  return res.data;
}
export async function getActivityRef(id: string) {
  const res = await api.get<{ data: TimeActivityRef }>(`/time-master-data/activity-types/${id}`);
  return res.data.data;
}
export async function createActivityRef(payload: Partial<TimeActivityRef>) {
  const res = await api.post<{ data: TimeActivityRef }>('/time-master-data/activity-types', payload);
  return res.data.data;
}
export async function updateActivityRef(id: string, payload: Partial<TimeActivityRef>) {
  const res = await api.patch<{ data: TimeActivityRef }>(`/time-master-data/activity-types/${id}`, payload);
  return res.data.data;
}
export async function deleteActivityRef(id: string) {
  const res = await api.delete<{ data: TimeActivityRef }>(`/time-master-data/activity-types/${id}`);
  return res.data.data;
}

// ── Users ───────────────────────────────────────────────────────────────────

export async function listUserRefs(params: ListParams & { linked?: 'true' | 'false' } = {}) {
  const res = await api.get<PaginatedResponse<TimeUserRef>>('/time-master-data/users', { params });
  return res.data;
}
export async function getUserRef(id: string) {
  const res = await api.get<{ data: TimeUserRef }>(`/time-master-data/users/${id}`);
  return res.data.data;
}
export async function createUserRef(payload: Partial<TimeUserRef>) {
  const res = await api.post<{ data: TimeUserRef }>('/time-master-data/users', payload);
  return res.data.data;
}
export async function updateUserRef(id: string, payload: Partial<TimeUserRef>) {
  const res = await api.patch<{ data: TimeUserRef }>(`/time-master-data/users/${id}`, payload);
  return res.data.data;
}
export async function deleteUserRef(id: string) {
  const res = await api.delete<{ data: TimeUserRef }>(`/time-master-data/users/${id}`);
  return res.data.data;
}

export async function linkUserRef(id: string, userId: string | null) {
  const res = await api.patch<{ data: TimeUserRef }>(
    `/time-master-data/users/${id}/link`,
    { userId },
  );
  return res.data.data;
}

// ── CustomFields-Schemata (AP-09-C) ────────────────────────────────────────

export async function getRefSchemas() {
  const res = await api.get<RefSchemasResponse>('/time-master-data/schemas');
  return res.data;
}

export async function updateRefSchemas(payload: UpdateRefSchemasPayload) {
  const res = await api.patch<RefSchemasResponse>('/time-master-data/schemas', payload);
  return res.data;
}
